import { useState, useRef, useEffect } from 'react'
import {
  AdvisorBrand,
  getAdvisorBrand,
  setAdvisorBrand,
  clearAdvisorBrand,
} from '@utils/advisorBrand'
import './AdvisorBrandPanel.css'

interface AdvisorBrandPanelProps {
  onSaved: (brand: AdvisorBrand) => void
  defaultExpanded?: boolean
}

export default function AdvisorBrandPanel({ onSaved, defaultExpanded = true }: AdvisorBrandPanelProps) {
  const existing = getAdvisorBrand()
  const [expanded, setExpanded] = useState(defaultExpanded)
  // Sync external prop changes so the controller can re-open the panel after
  // mount (e.g. when a no-brand Export PDF click fires while the panel is
  // collapsed). Without this useEffect, useState only honors defaultExpanded
  // on initial mount.
  useEffect(() => {
    setExpanded(defaultExpanded)
  }, [defaultExpanded])
  const [firmName, setFirmName] = useState(existing?.firmName ?? '')
  const [advisorName, setAdvisorName] = useState(existing?.advisorName ?? '')
  const [footerNote, setFooterNote] = useState(existing?.footerNote ?? '')
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(existing?.logoDataUrl)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null)
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result || '')
      setLogoDataUrl(dataUrl)
    }
    reader.onerror = () => setError('Could not read the selected file')
    reader.readAsDataURL(file)
  }

  function handleSave() {
    setError(null)
    if (!firmName.trim()) {
      setError('Firm name is required')
      return
    }
    try {
      setAdvisorBrand({
        firmName: firmName.trim(),
        advisorName: advisorName.trim() || undefined,
        logoDataUrl: logoDataUrl,
        footerNote: footerNote.trim() || undefined,
      })
      const saved = getAdvisorBrand()
      if (saved) onSaved(saved)
      setExpanded(false)
    } catch (e: any) {
      setError(e?.message ?? 'Could not save brand')
    }
  }

  function handleReset() {
    clearAdvisorBrand()
    setFirmName('')
    setAdvisorName('')
    setFooterNote('')
    setLogoDataUrl(undefined)
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="advisor-brand-panel">
      <button
        type="button"
        className="advisor-brand-panel__toggle"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        {expanded ? 'Hide' : 'Edit'} advisor brand
      </button>
      {expanded && (
        <div className="advisor-brand-panel__body">
          <label className="advisor-brand-panel__field">
            Firm name
            <input
              type="text"
              value={firmName}
              maxLength={80}
              onChange={(e) => setFirmName(e.target.value)}
            />
          </label>
          <label className="advisor-brand-panel__field">
            Your name
            <input
              type="text"
              value={advisorName}
              maxLength={80}
              onChange={(e) => setAdvisorName(e.target.value)}
            />
          </label>
          <label className="advisor-brand-panel__field">
            Firm logo (PNG or JPEG, up to 200KB)
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleLogoChange}
            />
          </label>
          {logoDataUrl && (
            <img className="advisor-brand-panel__preview" src={logoDataUrl} alt="Logo preview" />
          )}
          <label className="advisor-brand-panel__field">
            Footer note (shown on PDF cover)
            <input
              type="text"
              value={footerNote}
              maxLength={200}
              placeholder="Powered by Causeway. Strategy that explains itself."
              onChange={(e) => setFooterNote(e.target.value)}
            />
          </label>
          {error && <div className="advisor-brand-panel__error">{error}</div>}
          <div className="advisor-brand-panel__actions">
            <button type="button" className="btn btn--primary" onClick={handleSave}>
              Save
            </button>
            <button type="button" className="btn btn--ghost" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
